import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import appInfo from '../constants/appInfo';
import './PrivacyPage.css';

function PrivacyPage() {
  return (
    <div className="privacy-page">
      <Navbar />
      <main className="privacy-page__main">
        <div className="privacy-page__container">

          <div className="privacy-page__breadcrumb">
            <Link to="/" className="privacy-page__back">← Back to Home</Link>
          </div>

          <h1 className="privacy-page__title">Privacy Policy for {appInfo.name}</h1>
          <p className="privacy-page__updated">Last updated: August 1, 2026</p>

          <div className="privacy-page__body">

            <section className="privacy-page__section">
              <p>
                {appInfo.name} ("we", "our", "us", or "the App") is a farm and livestock
                management application that helps farmers track and manage animal health
                records, including vaccination, deworming, medical treatment, and breeding
                details, and sends notifications about the health status of their animals.
              </p>
              <p>
                This Privacy Policy explains what information we collect, how we use it, and
                the choices you have. By using {appInfo.name}, you agree to the practices
                described in this policy.
              </p>
            </section>

            <section className="privacy-page__section">
              <h2>1. Information We Collect</h2>

              <h3>1.1 Account and Login Information</h3>
              <p>
                {appInfo.name} uses phone number-based login. When you sign up or log in, we
                collect your <strong>mobile phone number</strong> and verify it using a
                one-time password (OTP). You may update your registered phone number at any
                time from within the App.
              </p>

              <h3>1.2 Profile Information</h3>
              <ul>
                <li>Farmer name</li>
                <li>Farm name</li>
                <li>Phone number</li>
                <li>Farm profile photo (optional, added by the farmer)</li>
              </ul>
              <p>You can view and edit your profile information at any time within the App.</p>

              <h3>1.3 Farm and Animal Records</h3>
              <p>
                {appInfo.name} lets you record and manage details about your farm animals
                (such as cows and buffaloes), including:
              </p>
              <ul>
                <li>Animal identity and basic details (breed, age, etc.)</li>
                <li>Vaccination records</li>
                <li>Deworming schedules</li>
                <li>Medical treatment history</li>
                <li>Breeding information</li>
                <li>
                  Photos of animals, captured using your device camera or selected from your
                  photo gallery, to help identify and document each animal
                </li>
              </ul>

              <h3>1.4 Doctor / Veterinarian Information</h3>
              <p>
                You may manually enter the name and phone number of your doctor or
                veterinarian within the App, so you can access this information quickly when
                needed. This is entered directly by you as text — {appInfo.name} does not
                access, read, or import your device's phone contacts list.
              </p>

              <h3>1.5 Notifications</h3>
              <p>
                {appInfo.name} sends you notifications and reminders — for example, about
                upcoming vaccinations, deworming schedules, or medical checkups for your
                animals.
              </p>

              <h3>1.6 App Preferences</h3>
              <p>
                We store your selected app language preference so the App displays content
                in your chosen language.
              </p>

              <h3>1.7 Automatically Collected Information</h3>
              <p>
                We may collect basic device information (such as device type and operating
                system version) and general app usage information to help us maintain and
                improve the App. {appInfo.name} does not currently use any analytics or
                crash-reporting SDKs, and does not display advertisements.
              </p>
            </section>

            <section className="privacy-page__section">
              <h2>2. How We Use Your Information</h2>
              <ul>
                <li>To verify your identity and let you log in securely using your phone number and OTP.</li>
                <li>To store and organize your farm, animal, vaccination, deworming, medical, and breeding records.</li>
                <li>To send you timely notifications and reminders about upcoming vaccinations, deworming schedules, or medical checkups.</li>
                <li>To let you attach photos to animal records and your farm profile for identification and documentation.</li>
                <li>To display your doctor/veterinarian's saved contact details when you need them.</li>
                <li>To display the App in your preferred language.</li>
                <li>To maintain, secure, and improve the App's performance and features.</li>
                <li>To provide customer support when you contact us with questions or issues.</li>
              </ul>
            </section>

            <section className="privacy-page__section">
              <h2>3. How We Share Your Information</h2>
              <p>
                We do not sell your personal data. We do not display third-party ads. We may
                share information only in the following circumstances:
              </p>
              <ul>
                <li>
                  <strong>Service provider:</strong> We use Google Firebase for
                  authentication (phone/OTP login), database storage, and push
                  notifications. Firebase processes data on our behalf under Google's data
                  processing terms and is bound by confidentiality obligations. We do not
                  use any other third-party service to process your personal data.
                </li>
                <li><strong>Legal requirements:</strong> If required by law, regulation, or valid legal process.</li>
              </ul>
            </section>

            <section className="privacy-page__section">
              <h2>4. Data Storage and Security</h2>
              <p>
                {appInfo.name} is built using Expo (React Native) for the app and Google
                Firebase for backend services, including authentication, database storage,
                and notifications. Your data is stored securely on Firebase's infrastructure
                using industry-standard practices, including encrypted connections and
                access controls. While we take reasonable steps to protect your information,
                no method of electronic storage or transmission is 100% secure.
              </p>
            </section>

            <section className="privacy-page__section">
              <h2>5. Data Retention</h2>
              <p>
                We retain your farm, animal, and profile records for as long as your account
                remains active. If you delete your account, your data will be deleted as
                described in Section 6.
              </p>
            </section>

            <section className="privacy-page__section">
              <h2>6. Account Deletion and Your Choices</h2>
              <ul>
                <li>You can update your profile details (name, farm name, farm photo) and your phone number at any time within the App.</li>
                <li>You can delete individual animal records at any time within the App.</li>
                <li>
                  <strong>Account deletion:</strong> {appInfo.name} provides an in-app
                  "Delete Account" option. Using this option permanently deletes your
                  account and all associated data (profile, animal records,
                  vaccination/medical/breeding history, and doctor information) from our
                  systems.
                </li>
                <li>You can also request account or data deletion by contacting us at the email address in Section 9.</li>
              </ul>
            </section>

            <section className="privacy-page__section">
              <h2>7. Children's Privacy</h2>
              <p>
                {appInfo.name} is intended for use by farmers and farm operators and is not
                directed at children under 13. We do not knowingly collect personal
                information from children.
              </p>
            </section>

            <section className="privacy-page__section">
              <h2>8. Changes to This Privacy Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. Changes will be posted
                on this page with an updated "Last updated" date. Continued use of the App
                after changes constitutes acceptance of the revised policy.
              </p>
            </section>

            <section className="privacy-page__section">
              <h2>9. Contact Us</h2>
              <p>
                If you have questions about this Privacy Policy, how your data is handled,
                or wish to request deletion of your account and data, please contact us at:
              </p>
              <p className="privacy-page__contact">
                <strong>Email:</strong>{' '}
                <a href={`mailto:${appInfo.supportEmail}`} className="privacy-page__link">
                  {appInfo.supportEmail}
                </a>
                <br />
                <strong>App name:</strong> {appInfo.name}
              </p>
            </section>

          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default PrivacyPage;